import { Popconfirm } from '@/components/Popconfirm';
import { Text } from '@/components/ui';
import { SquarePenIcon, TrashIcon, RefreshCwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from './StatusBadge';
import { Progress } from '@/components/ui/Progress';
import { useMemo } from 'react';
import { format } from 'date-fns';
import { parseLocalDate } from '@/lib/utils/parse-local-date';
import { Contract } from '@console/utils';

interface ContractCardProps {
  contract: Contract;
  onEdit: () => void;
  onDelete: () => void;
  onRenew?: () => void;
}

export function ContractCard({ contract, onEdit, onDelete, onRenew }: ContractCardProps) {
  const { t } = useTranslation();
  const usagePercentage = useMemo(
    () =>
      contract.contractedHours > 0 ? (contract.consumedHours * 100) / contract.contractedHours : 0,
    [contract.consumedHours, contract.contractedHours]
  );

  const canRenew = useMemo(() => {
    return ['EXPIRED', 'EXPIRING'].includes(contract.status) && contract.isActive;
  }, [contract.status, contract.isActive]);

  return (
    <div className="p-md border-transparent-dark-16 rounded-2xl border shadow-xs">
      <header className="gap-2xs flex items-start justify-between">
        <div className="gap-4xs flex min-w-0 flex-1 items-center">
          <Text size="lg" strong className="truncate">
            {contract.company.name}
          </Text>
          <StatusBadge status={contract.status} />
        </div>
        <div className="gap-2xs flex items-center">
          <Popconfirm
            onConfirm={onDelete}
            title={t('contracts.card.deleteAction.title')}
            description={t('contracts.card.deleteAction.description')}
            variant="danger"
            confirmLabel={t('contracts.card.deleteAction.label')}
          >
            <button
              type="button"
              className="text-neutral-muted hover:text-error-default outline-neutral-subtle cursor-pointer rounded-full transition-all focus:outline-2 focus:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              title={t('contracts.card.deleteContract')}
            >
              <TrashIcon size={16} />
            </button>
          </Popconfirm>
          <button
            type="button"
            className="text-neutral-muted hover:text-neutral-default outline-neutral-subtle cursor-pointer rounded-full transition-all focus:outline-2 focus:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={onEdit}
            title={t('contracts.card.editContract')}
          >
            <SquarePenIcon size={16} />
          </button>
          {canRenew && onRenew && (
            <button
              type="button"
              className="text-neutral-muted hover:text-neutral-default outline-neutral-subtle cursor-pointer rounded-full transition-all focus:outline-2 focus:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onRenew}
              title={t('contracts.card.renewContract')}
            >
              <RefreshCwIcon size={16} />
            </button>
          )}
        </div>
      </header>

      <div className="mt-md gap-4xs flex flex-col">
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('contracts.card.startDate')}</Text>
          <Text strong>
            {contract.startDate ? format(parseLocalDate(contract.startDate), 'dd/MM/yyyy') : '-'}
          </Text>
        </div>
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('contracts.card.endDate')}</Text>
          <Text strong>
            {contract.endDate ? format(parseLocalDate(contract.endDate), 'dd/MM/yyyy') : '-'}
          </Text>
        </div>
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('contracts.card.contractedHours')}</Text>
          <Text strong>{contract.contractedHours}h</Text>
        </div>
        {contract.monthlyHoursLimit && contract.monthlyHoursLimit > 0 && (
          <div className="flex justify-between">
            <Text className="text-neutral-muted">{t('contracts.card.monthlyHoursLimit')}</Text>
            <Text strong>
              {contract.monthlyHoursLimit}h/{t('common.month')}
            </Text>
          </div>
        )}
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('contracts.card.consumedHours')}</Text>
          <Text strong>{contract.consumedHours}h</Text>
        </div>
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('contracts.card.remainingHours')}</Text>
          <Text strong>{contract.remainingHours}h</Text>
        </div>
        <div className="flex justify-between">
          <Text className="text-neutral-muted">{t('contracts.card.accumulateHours')}</Text>
          <Text strong>{contract.accumulateHours ? t('common.yes') : t('common.no')}</Text>
        </div>
        {contract.excludeProactiveTickets && (
          <div className="flex justify-between">
            <Text className="text-neutral-muted">{t('contracts.card.proactiveMonitoring')}</Text>
            <Text strong>{t('contracts.card.proactiveNotDebited')}</Text>
          </div>
        )}
      </div>

      <footer className="mt-md space-y-md">
        <Progress value={usagePercentage} />
      </footer>
    </div>
  );
}
